import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    authAPI.validateResetToken(token)
      .then(r => setEmail(r.data.email))
      .catch(err => setError(err.response?.data?.detail || 'Invalid or expired reset link.'))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      await authAPI.resetPassword(token, password);
      setSuccess(true);
      toast.success('Password reset successfully!');
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
    setSubmitting(false);
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#E31937] mx-auto mb-2" />
        <p className="text-gray-400 text-sm">Validating reset link...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="reset-error">
      <Card className="w-full max-w-sm bg-white border-gray-200 text-center">
        <CardContent className="pt-8 pb-6">
          <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
          <p className="text-red-500 font-medium mb-2">{error}</p>
          <p className="text-xs text-gray-400 mb-4">Please request a new reset link.</p>
          <Button variant="outline" onClick={() => navigate('/login')} data-testid="back-to-login">Back to Login</Button>
        </CardContent>
      </Card>
    </div>
  );

  if (success) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="reset-success">
      <Card className="w-full max-w-sm bg-white border-gray-200 text-center">
        <CardContent className="pt-8 pb-6">
          <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-gray-900 mb-1">Password Reset!</h2>
          <p className="text-sm text-gray-500 mb-1">Your password has been updated successfully.</p>
          <p className="text-xs text-gray-400">Redirecting to login...</p>
          <Loader2 className="w-4 h-4 animate-spin text-[#E31937] mx-auto mt-4" />
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-testid="reset-password-page">
      <Card className="w-full max-w-sm bg-white border-gray-200">
        <CardHeader className="text-center pb-4">
          <img src="https://customer-assets.emergentagent.com/job_12492ec0-1e26-4f4b-96d9-e0a655349093/artifacts/4uh3nwhf_image.png" alt="CTS" className="h-12 mx-auto mb-3" />
          <CardTitle className="text-lg text-gray-900">Reset Password</CardTitle>
          <p className="text-xs text-gray-400">Enter your new password for {email}</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-3">
            <Input
              type="password"
              placeholder="New Password (min. 6 characters)"
              value={password}
              onChange={e => setPassword(e.target.value)}
              className="bg-gray-100 border-gray-200 text-gray-900"
              data-testid="reset-new-password"
              required
            />
            <Input
              type="password"
              placeholder="Confirm Password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              className="bg-gray-100 border-gray-200 text-gray-900"
              data-testid="reset-confirm-password"
              required
            />
            <Button type="submit" className="w-full bg-[#E31937] hover:bg-[#C4122C] text-white" disabled={submitting} data-testid="reset-submit">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Resetting...</> : 'Reset Password'}
            </Button>
          </form>
          <p className="text-center text-xs text-gray-400 mt-3">
            <a href="/login" className="text-[#E31937] hover:underline">Back to Login</a>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
